// @flow
import type { Node } from 'react';

import React from 'react';
// eslint-disable-next-line import/extensions
import { FaCheck, FaPen, FaTimes } from 'react-icons/fa';
import { Button } from 'reactstrap';
import getFullyQualifiedFieldName from '#support/utility/form/getFullyQualifiedFieldName.js';
import { FieldPopulationToggle } from '#components/pages/Models/Model/support/form/index.js';
import { EnhancedField } from '#components/support/forms/utility/index.js';
import AiFieldMarking from './AiFieldMarking/AiFieldMarking.jsx';
import {
  toggleLayout,
  fieldWrapper,
  toggleWrapper,
  aiLabelRow,
  aiChipOnlyRow,
  aiControlsRow,
  aiControl,
  aiControlQuiet,
  proposedField,
} from './ModelFormField.module.scss';

// TODO: considering updating the EnhancedField to cover the additional functionality added here

type Props = {
  excludeLabel?: boolean,
  excludeFeedback?: boolean,
  excludeTip?: boolean,
  excludePopulationToggle?: boolean,
  excludeInputGroup?: boolean,
  field: Object,
  item?: string | null,
  render?: Function,
  visible: boolean,
  autofilled?: boolean,
  readOnly?: boolean,
  aiStatus?: 'none' | 'proposed' | 'accepted',
  provenance?: ?Object,
  onAcceptProposal?: Function,
  onRejectProposal?: Function,
};

const ModelFormField = ({
  excludeLabel = false,
  excludeFeedback = false,
  excludeTip = false,
  excludePopulationToggle = false,
  excludeInputGroup = false,
  field,
  item = null,
  render = null,
  visible,
  autofilled = false,
  readOnly = false,
  aiStatus = 'none',
  provenance = null,
  onAcceptProposal = null,
  onRejectProposal = null,
}: Props): Node => {
  if (!visible) {
    return null;
  }

  // AI-review marking (UI design §3.1) is a minimal decoration on top of the ordinary
  // field: built on the existing autofill-marking pattern (readonly styling), with a
  // label chip, a provenance affordance, and accept/reject controls (the reject control
  // is labeled "Edit" once a value is accepted -- same transition, §3.1/§3.4). A field is
  // never both AI-marked and autofilled (data & API design §2.1), so this takes priority
  // over the population-toggle branch below without needing to check. The chip and
  // controls sit outside the input's own row (label row above, controls row below) so
  // the input renders at the same width as an unmarked field (§3.1) -- unlike the
  // population-toggle layout below, which shares a flex row with its control by design.
  if (aiStatus !== 'none') {
    const fullyQualifiedName = getFullyQualifiedFieldName(item, field.name);
    const aiMarkingWrapper = (labelElement, fieldElement, feedbackElement) => (
      <>
        <div className={labelElement ? aiLabelRow : `${aiLabelRow} ${aiChipOnlyRow}`}>
          {labelElement}
          <AiFieldMarking
            aiStatus={aiStatus}
            provenance={provenance}
            fieldId={fullyQualifiedName}
          />
        </div>
        <div className={aiStatus === 'proposed' ? proposedField : undefined}>{fieldElement}</div>
        {feedbackElement}
        <div className={aiControlsRow}>
          {aiStatus === 'proposed' && (
            <Button
              color="primary"
              outline
              size="sm"
              className={aiControl}
              id={`${fullyQualifiedName}AcceptProposal`}
              onClick={onAcceptProposal}
            >
              <FaCheck aria-hidden="true" />
              Accept
            </Button>
          )}
          {aiStatus === 'proposed' && (
            <Button
              color="link"
              size="sm"
              className={`${aiControl} ${aiControlQuiet}`}
              id={`${fullyQualifiedName}RejectProposal`}
              onClick={onRejectProposal}
            >
              <FaTimes aria-hidden="true" />
              Reject
            </Button>
          )}
          {aiStatus === 'accepted' && (
            <Button
              color="link"
              size="sm"
              className={`${aiControl} ${aiControlQuiet}`}
              id={`${fullyQualifiedName}EditField`}
              onClick={onRejectProposal}
            >
              <FaPen aria-hidden="true" />
              Edit
            </Button>
          )}
        </div>
      </>
    );

    return (
      <EnhancedField
        excludeLabel={excludeLabel}
        excludeFeedback={excludeFeedback}
        excludeTip={excludeTip}
        excludeInputGroup={excludeInputGroup}
        field={field}
        item={item}
        render={render}
        visible={visible}
        validation={field.validation}
        autofilled
        customWrapper={aiMarkingWrapper}
      />
    );
  }

  const showPopulationToggle = !excludePopulationToggle && field.autofiller;

  // Use EnhancedField for simple cases (no population toggle)
  if (!showPopulationToggle) {
    return (
      <EnhancedField
        excludeLabel={excludeLabel}
        excludeFeedback={excludeFeedback}
        excludeTip={excludeTip}
        excludeInputGroup={excludeInputGroup}
        field={field}
        item={item}
        render={render}
        visible={visible}
        validation={field.validation}
        autofilled={autofilled}
        readOnly={readOnly}
      />
    );
  }

  // Custom wrapper for population toggle layout
  const populationToggleWrapper = (labelElement, fieldElement, feedbackElement) => {
    const fullyQualifiedName = getFullyQualifiedFieldName(item, field.name);
    const populationToggleElement = (
      <FieldPopulationToggle fieldName={fullyQualifiedName} form={field.form} />
    );

    return (
      <>
        {labelElement}
        <div className={toggleLayout}>
          <div className={fieldWrapper}>
            {fieldElement}
            {feedbackElement}
          </div>
          <div className={toggleWrapper}>{populationToggleElement}</div>
        </div>
      </>
    );
  };

  return (
    <EnhancedField
      excludeLabel={excludeLabel}
      excludeFeedback={excludeFeedback}
      excludeTip={excludeTip}
      excludeInputGroup={excludeInputGroup}
      field={field}
      item={item}
      render={render}
      visible={visible}
      validation={field.validation}
      autofilled={autofilled}
      readOnly={readOnly}
      customWrapper={populationToggleWrapper}
    />
  );
};

export default ModelFormField;
